import { Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { take } from 'rxjs';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CentroLogisticoCementoUpsertCommand } from '../../../models/centro-logistico-cemento.model';
import { CentroLogisticoCementoVm } from '../../../models/centro-logistico-cemento.vm';
import { CentrosLogisticosCementoStore } from '../../../stores/centros-logisticos-cemento.store';
import { CentrosLogisticosCementoUiEffectsService } from '../ui-effects/centros-logisticos-cemento-ui-effects.service';
import {
  AdminServerTableCellContext,
  AdminServerTableColumn,
  AdminServerTablePaginationState,
} from '../../../shared/table/models/admin-server-table.model';
import { buildCentrosLogisticosCementoColumns } from './centros-logisticos-cemento-table.columns';

const MODAL_CONFIRMACION: { centered: true; backdrop: 'static'; windowClass: string } = {
  centered: true,
  backdrop: 'static',
  windowClass: 'cbb-admin-modal-stack',
};

@Component({
  selector: 'app-centros-logisticos-cemento-tabla',
  templateUrl: './centros-logisticos-cemento-tabla.component.html',
  styleUrls: ['./centros-logisticos-cemento-tabla.component.css'],
})
export class CentrosLogisticosCementoTablaComponent implements OnInit {
  @ViewChild('detalleTpl', { static: true }) detalleTpl!: TemplateRef<unknown>;
  @ViewChild('cargaMasivaTpl', { static: true }) cargaMasivaTpl!: TemplateRef<unknown>;
  @ViewChild('confirmDeleteTpl', { static: true }) confirmDeleteTpl!: TemplateRef<unknown>;
  @ViewChild('confirmGuardarTpl', { static: true }) confirmGuardarTpl!: TemplateRef<unknown>;
  @ViewChild('actionsCellTpl', { static: true })
  actionsCellTpl!: TemplateRef<AdminServerTableCellContext<CentroLogisticoCementoVm>>;

  readonly store = inject(CentrosLogisticosCementoStore);
  private readonly modalService = inject(NgbModal);
  private readonly uiEffects = inject(CentrosLogisticosCementoUiEffectsService);

  private detalleModalRef: NgbModalRef | null = null;
  private cargaMasivaModalRef: NgbModalRef | null = null;
  private confirmDeleteModalRef: NgbModalRef | null = null;
  private confirmGuardarModalRef: NgbModalRef | null = null;
  private pendingUpsert: CentroLogisticoCementoUpsertCommand | null = null;
  tableColumns: AdminServerTableColumn<CentroLogisticoCementoVm>[] = [];

  selectedItem: CentroLogisticoCementoVm | null = null;

  ngOnInit(): void {
    this.tableColumns = buildCentrosLogisticosCementoColumns({
      actionsCellTemplate: this.actionsCellTpl,
    });
  }

  get tituloConfirmarGuardar(): string {
    return (this.selectedItem?.id ?? 0) > 0 ? 'Confirmar cambios' : 'Confirmar creación';
  }

  get mensajeConfirmarGuardar(): string {
    return (this.selectedItem?.id ?? 0) > 0
      ? '¿Desea guardar los cambios de este registro?'
      : '¿Desea crear este registro?';
  }

  get etiquetaConfirmarGuardar(): string {
    return (this.selectedItem?.id ?? 0) > 0 ? 'Guardar' : 'Crear';
  }

  abrirModalCargaMasiva(): void {
    const ref = this.modalService.open(this.cargaMasivaTpl, { centered: true });
    this.cargaMasivaModalRef = ref;
    ref.hidden.pipe(take(1)).subscribe(() => {
      if (this.cargaMasivaModalRef === ref) {
        this.cargaMasivaModalRef = null;
      }
    });
  }

  subirExcelCargaMasiva(file: File): void {
    this.store.subirExcelCargaMasiva(file).subscribe({
      next: () => {
        this.cargaMasivaModalRef?.dismiss();
        this.cargaMasivaModalRef = null;
        this.recargar(() => {
          this.uiEffects.showExcelSuccess();
        });
      },
      error: () => {
        this.uiEffects.showExcelError();
      },
    });
  }

  cerrarCargaMasivaModal(): void {
    this.cargaMasivaModalRef?.dismiss();
    this.cargaMasivaModalRef = null;
  }

  abrirCrear(): void {
    this.selectedItem = null;
    this.openDetalleModal();
  }

  abrirEditar(item: CentroLogisticoCementoVm): void {
    this.selectedItem = item;
    this.openDetalleModal();
  }

  prepararEliminar(item: CentroLogisticoCementoVm): void {
    this.selectedItem = item;
    this.solicitarEliminar();
  }

  guardar(item: CentroLogisticoCementoUpsertCommand): void {
    this.pendingUpsert = item;
    this.confirmGuardarModalRef = this.modalService.open(this.confirmGuardarTpl, MODAL_CONFIRMACION);
  }

  confirmarGuardar(): void {
    const item = this.pendingUpsert;
    const selectedItem = this.selectedItem;
    this.pendingUpsert = null;
    this.confirmGuardarModalRef?.dismiss();
    this.confirmGuardarModalRef = null;

    if (!item) {
      return;
    }

    const request$ = selectedItem
      ? this.store.editarItem(selectedItem.id, item)
      : this.store.crearItem(item);

    this.detalleModalRef?.dismiss();
    this.detalleModalRef = null;

    request$.subscribe({
      next: () => {
        this.recargar(() => {
          if (selectedItem) {
            this.uiEffects.showUpdateSuccess();
            return;
          }
          this.uiEffects.showCreateSuccess();
        });
      },
      error: () => {
        if (selectedItem) {
          this.uiEffects.showUpdateError();
          return;
        }
        this.uiEffects.showCreateError();
      },
    });
  }

  cancelarGuardar(): void {
    this.pendingUpsert = null;
    this.confirmGuardarModalRef?.dismiss();
    this.confirmGuardarModalRef = null;
  }

  solicitarEliminar(): void {
    if ((this.selectedItem?.id ?? 0) < 1) {
      return;
    }

    this.confirmDeleteModalRef = this.modalService.open(this.confirmDeleteTpl, MODAL_CONFIRMACION);
  }

  confirmarEliminar(): void {
    const id = this.selectedItem?.id ?? 0;
    this.confirmDeleteModalRef?.dismiss();
    this.confirmDeleteModalRef = null;

    if (id < 1 || this.store.loading()) {
      return;
    }

    this.detalleModalRef?.dismiss();
    this.detalleModalRef = null;
    this.store.eliminarItem(id).subscribe({
      next: () => {
        this.recargar(() => {
          this.uiEffects.showDeleteSuccess();
        });
      },
      error: () => {
        this.uiEffects.showDeleteError();
      },
    });
  }

  cancelarEliminar(): void {
    this.confirmDeleteModalRef?.dismiss();
    this.confirmDeleteModalRef = null;
  }

  cerrarDetalle(): void {
    this.detalleModalRef?.dismiss();
    this.detalleModalRef = null;
    this.selectedItem = null;
  }

  onSearchTermChange(value: string | number): void {
    this.store.setSearchTerm(String(value ?? ''));
  }

  onOrderingChange(value: string): void {
    this.store.setOrdering(value);
  }

  onPageChange(value: number): void {
    this.store.setPage(value);
  }

  onPageSizeChange(value: number): void {
    this.store.setPageSize(value);
  }

  get paginationState(): AdminServerTablePaginationState {
    return {
      page: this.store.page(),
      pageSize: this.store.pageSize(),
      total: this.store.totalItems(),
      totalPages: this.store.totalPages(),
      hasPreviousPage: this.store.hasPreviousPage(),
      hasNextPage: this.store.hasNextPage(),
      pageStartIndex: this.store.pageStartIndex(),
      pageEndIndex: this.store.pageEndIndex(),
      pageSizeOptions: [10, 20, 50, 100],
    };
  }

  readonly trackByItem = (_: number, item: CentroLogisticoCementoVm): string => item.trackId;

  private openDetalleModal(): void {
    const ref = this.modalService.open(this.detalleTpl, {
      centered: true,
      backdrop: 'static',
      size: 'lg',
    });
    this.detalleModalRef = ref;
    ref.hidden.pipe(take(1)).subscribe(() => {
      if (this.detalleModalRef === ref) {
        this.detalleModalRef = null;
      }
      this.selectedItem = null;
    });
  }

  private recargar(onSuccess?: () => void): void {
    this.store.clearError();
    this.store.loadItems().subscribe({
      next: () => {
        onSuccess?.();
      },
      error: () => {
        this.uiEffects.showLoadError();
      },
    });
  }
}
